import { apiClient } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type PromotionCurrency = 'ALL' | 'EUR' | 'USD';

export interface Promotion {
  _id: string;
  title: string;
  description: string;
  photo: string;
  price: number;
  discountedPrice?: number;
  currency?: PromotionCurrency;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePromotionData {
  title: string;
  description: string;
  photo: string;
  price: number;
  discountedPrice?: number;
  currency?: PromotionCurrency;
  startDate: string;
  endDate: string;
  isActive?: boolean;
}

export function formatPromotionPrice(amount: number, currency?: PromotionCurrency | string | null): string {
  const c = currency || 'ALL';
  if (c === 'USD') return `$${amount.toFixed(2)}`;
  if (c === 'EUR') return `€${amount.toFixed(2)}`;
  return `${amount.toFixed(2)} ALL`;
}

export type UpdatePromotionData = Partial<CreatePromotionData>;

// API Functions
export const getPromotions = async (): Promise<Promotion[]> => {
  const response = await apiClient.get('/promotions');
  return response.data;
};

export const getPromotion = async (id: string): Promise<Promotion> => {
  const response = await apiClient.get(`/promotions/${id}`);
  return response.data;
};

export const createPromotion = async (data: CreatePromotionData): Promise<Promotion> => {
  const response = await apiClient.post('/promotions', data);
  return response.data;
};

export const updatePromotion = async (
  id: string,
  data: UpdatePromotionData
): Promise<Promotion> => {
  const response = await apiClient.patch(`/promotions/${id}`, data);
  return response.data;
};

export const deletePromotion = async (id: string): Promise<void> => {
  await apiClient.delete(`/promotions/${id}`);
};

// React Query Hooks
export const usePromotions = () => {
  return useQuery({
    queryKey: ['promotions'],
    queryFn: getPromotions,
  });
};

export const useCreatePromotion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPromotion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
};

export const useUpdatePromotion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePromotionData }) =>
      updatePromotion(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
};

export const useDeletePromotion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePromotion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
};
